import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Sidenav from "../components/Sidenav";
import { Modal } from "react-bootstrap";
import { Line } from "react-chartjs-2";
import { Chart, registerables } from "chart.js";
import "../Console.css";

import "leaflet/dist/leaflet.css";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  useMap,
  Pane,
} from "react-leaflet";
import L from "leaflet";

Chart.register(...registerables);

const NAVBAR_H = 94;
const SIDEBAR_W = 360;

// Rough national bounding box for Uganda
const UGANDA_BOUNDS = L.latLngBounds([-1.5, 29.3], [4.5, 35.1]);
const PADDING = 16;

// API
const API_URL =
  process.env.REACT_APP_BLEEP_API_URL || "https://api.iriscrypt.com/scan";
const API_KEY =
  process.env.REACT_APP_BLEEP_API_KEY || "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43";

// Status helpers
function getStatus(scan) {
  const serialStr = String(scan.serial ?? "");
  const lenOK = serialStr.length < 10;
  const validSerial = Boolean(scan.valid_serial ?? scan.validSerial);
  const validCdp = Boolean(scan.valid_cdp ?? scan.validCdp);

  if (!validSerial || !lenOK) return "invalid";
  if (validCdp) return "genuine";
  return "copy";
}

const markerColor = (status) =>
  status === "genuine" ? "#22c55e" : status === "copy" ? "#ef4444" : "#9ca3af";

const chipColors = (status) =>
  status === "genuine"
    ? { bg: "rgba(34,197,94,0.12)", text: "#166534", label: "Genuine" }
    : status === "copy"
      ? { bg: "rgba(239,68,68,0.12)", text: "#7f1d1d", label: "Copy" }
      : { bg: "rgba(156,163,175,0.18)", text: "#374151", label: "Invalid" };

// helper to check non-empty strings
const isNonEmpty = (v) => typeof v === "string" && v.trim().length > 0;

// Robust extractor for whatever the API returns
function extractImageUrl(payload) {
  const direct =
    payload?.images?.raw?.url ??
    payload?.images?.raw ??
    payload?.image_url ??
    payload?.imageUrl ??
    null;
  if (direct) return direct;

  if (Array.isArray(payload?.images) && payload.images.length) {
    const first = payload.images[0];
    return first?.url ?? first ?? null;
  }
  return null;
}

// Normalize API rows to our UI shape
function normalizeScan(s) {
  const ts = s.timestamp || new Date("2025-09-01").toISOString();

  // coordinates may come nested or flat
  const lat =
    Number(s.coordinates?.lat ?? s.lat ?? s.location?.lat ?? s.geo?.lat) ||
    null;
  const lng =
    Number(
      s.coordinates?.lng ?? s.lon ?? s.lng ?? s.location?.lng ?? s.geo?.lng,
    ) || null;

  // client name may appear under different keys
  const clientName = s.order_assignment?.client?.name ?? "Unknown";

  const productName = s.item?.metadata?.name ?? "Unassigned";

  const valid_serial = Boolean(s.valid_serial ?? s.validSerial);
  const valid_cdp = Boolean(s.valid_cdp ?? s.validCdp);

  // total times that serial has been scanned
  const serial_count = Number(s.repeat_scan_count ?? 1);

  // preserve images if present on the source row
  const imgUrl = s?.images ? extractImageUrl(s) : null;

  return {
    _id: s._id || s.id || String(Math.random()),
    serial: String(s.serial ?? ""),
    timestamp: ts,
    coordinates: { lat, lng },
    images: { raw: { url: imgUrl || null } }, // null when not provided by the API
    valid_serial,
    valid_cdp,
    client_name: clientName,
    product_name: productName,
    is_valid: Boolean(valid_serial && valid_cdp && isNonEmpty(clientName)),
    serial_count: Number.isFinite(serial_count) ? serial_count : 1,
  };
}

/** Fit to data points with sidebar-aware padding. Falls back to Uganda. */
function FitToData({ trigger, points }) {
  const map = useMap();
  useEffect(() => {
    if (!trigger) return;

    setTimeout(() => {
      map.invalidateSize();

      const valid = points
        .map((p) => (Array.isArray(p) ? p : null))
        .filter((p) => p && Number.isFinite(p[0]) && Number.isFinite(p[1]));

      const paddingTopLeft = [PADDING, PADDING];
      const paddingBottomRight = [SIDEBAR_W + PADDING, PADDING];

      if (valid.length === 0) {
        if (map.flyToBounds) {
          map.flyToBounds(UGANDA_BOUNDS, {
            paddingTopLeft,
            paddingBottomRight,
            maxZoom: 9,
            duration: 0.6,
          });
        } else {
          map.fitBounds(UGANDA_BOUNDS, {
            paddingTopLeft,
            paddingBottomRight,
            maxZoom: 9,
            animate: true,
          });
        }
        return;
      }

      const unique = Array.from(new Set(valid.map((v) => v.join(",")))).map(
        (k) => k.split(",").map(Number),
      );

      if (unique.length === 1) {
        map.flyTo(unique[0], 13, { duration: 0.6 });
        return;
      }

      const bounds = L.latLngBounds(unique);
      if (map.flyToBounds) {
        map.flyToBounds(bounds, {
          paddingTopLeft,
          paddingBottomRight,
          maxZoom: 16,
          duration: 0.6,
        });
      } else {
        map.fitBounds(bounds, {
          paddingTopLeft,
          paddingBottomRight,
          maxZoom: 16,
          animate: true,
        });
      }
    }, 0);
  }, [trigger, points, map]);

  return null;
}

// Build daily time series from scans with 3 buckets
const toDayKey = (ts) => new Date(ts).toISOString().slice(0, 10);
function makeDailySeries(scans) {
  if (!scans?.length) return { labels: [], genuine: [], copy: [], invalid: [] };

  const withTs = scans.filter((s) => !isNaN(new Date(s.timestamp)));
  if (!withTs.length) return { labels: [], genuine: [], copy: [], invalid: [] };

  let min = new Date(withTs[0].timestamp);
  let max = new Date(withTs[0].timestamp);
  for (const s of withTs) {
    const d = new Date(s.timestamp);
    if (d < min) min = d;
    if (d > max) max = d;
  }

  const start = new Date(
    Date.UTC(min.getUTCFullYear(), min.getUTCMonth(), min.getUTCDate()),
  );
  const end = new Date(
    Date.UTC(max.getUTCFullYear(), max.getUTCMonth(), max.getUTCDate()),
  );
  const labels = [];
  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    labels.push(d.toISOString().slice(0, 10));
  }

  const idx = Object.fromEntries(labels.map((l, i) => [l, i]));
  const genuine = new Array(labels.length).fill(0);
  const copy = new Array(labels.length).fill(0);
  const invalid = new Array(labels.length).fill(0);

  for (const s of withTs) {
    const key = toDayKey(s.timestamp);
    const i = idx[key];
    if (i == null) continue;

    const status = getStatus(s);
    if (status === "genuine") genuine[i] += 1;
    else if (status === "copy") copy[i] += 1;
    else invalid[i] += 1;
  }

  return { labels, genuine, copy, invalid };
}

const BleSTMap = ({ domain, userData }) => {
  const navigate = useNavigate();

  const userType = useMemo(
    () => (userData?.[1] || "").toString().toLowerCase().trim(),
    [userData],
  );
  const clientIdFromUser = useMemo(
    () => (userData?.[5] || "").toString().trim(),
    [userData],
  );
  const isBrandAdmin = userType === "brandadmin";

  // Kept in a ref so loadPage (stable useCallback) can always see the latest value
  const clientIdRef = useRef("");
  useEffect(() => {
    clientIdRef.current =
      isBrandAdmin && clientIdFromUser ? clientIdFromUser : "";
  }, [isBrandAdmin, clientIdFromUser]);

  // Tracks when userData is fully populated so loadPage guards against premature fetches
  const userReadyRef = useRef(false);
  useEffect(() => {
    userReadyRef.current = Array.isArray(userData) && userData.length >= 6;
  }, [userData]);

  // Period filter for map + chart
  const [period, setPeriod] = useState("today");
  const periodLabel = useMemo(() => {
    switch (period) {
      case "today":
        return "Today";
      case "week":
        return "Last 7 days";
      case "month":
        return "Last 30 days";
      case "year":
        return "Last 12 months";
      case "all":
      default:
        return "All time";
    }
  }, [period]);

  // Totals from API
  const [totalAll, setTotalAll] = useState(null);
  const [totalFiltered, setTotalFiltered] = useState(null);

  // All scans for map + chart (fetched once from /scan with period)
  const [scansAll, setScansAll] = useState([]);
  const [loadingAll, setLoadingAll] = useState(true);

  // Paged scans for sidebar list (/scan/paged?page=N)
  const [scansPaged, setScansPaged] = useState([]);
  const [currentPage, setCurrentPage] = useState(0); // highest loaded page
  const [totalPages, setTotalPages] = useState(null);
  const [loadingPage, setLoadingPage] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState(null);

  const mapRef = useRef(null);
  const [fitTrigger, setFitTrigger] = useState(0);
  const [modalScan, setModalScan] = useState(null);
  const [idCopied, setIdCopied] = useState(false);
  const [coordsCopied, setCoordsCopied] = useState(false);
  const sidebarRef = useRef(null);
  const sentinelRef = useRef(null);

  // StrictMode/dup-call guards for paged only
  const loadingRef = useRef(false);
  const loadedPagesRef = useRef(new Set());

  // Lightbox state
  const [lightbox, setLightbox] = useState({
    open: false,
    src: "",
    alt: "",
  });

  const openLightbox = (src, alt = "Scan image") => {
    setLightbox({ open: true, src, alt });
  };
  const closeLightbox = () => {
    setLightbox({ open: false, src: "", alt: "" });
  };
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") closeLightbox();
    };
    if (lightbox.open) document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [lightbox.open]);

  const closeModal = () => setModalScan(null);

  const copyId = () => {
    if (!modalScan) return;
    navigator.clipboard.writeText(modalScan._id).then(() => {
      setIdCopied(true);
      setTimeout(() => setIdCopied(false), 2000);
    });
  };

  const copyCoords = (lat, lng) => {
    navigator.clipboard
      .writeText(`${lat.toFixed(6)}, ${lng.toFixed(6)}`)
      .then(() => {
        setCoordsCopied(true);
        setTimeout(() => setCoordsCopied(false), 2000);
      });
  };

  // Per scan image caches and loaders for map popups
  const [imgById, setImgById] = useState({}); // { [id]: url }
  const [imgLoading, setImgLoading] = useState({}); // { [id]: bool }
  const [imgError, setImgError] = useState({}); // { [id]: string|null }

  async function fetchImageForId(id) {
    if (!id || imgById[id] || imgLoading[id]) return;

    setImgLoading((m) => ({ ...m, [id]: true }));
    setImgError((m) => ({ ...m, [id]: null }));
    try {
      const res = await fetch(`${API_URL}/by-id/${id}`, {
        headers: { "x-api-key": API_KEY, Accept: "application/json" },
      });
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`API ${res.status}${t ? `: ${t}` : ""}`);
      }
      const data = await res.json().catch(() => ({}));
      const url = extractImageUrl(data) || null;
      if (!url) throw new Error("No image found for this scan");
      setImgById((m) => ({ ...m, [id]: url }));
    } catch (e) {
      setImgError((m) => ({
        ...m,
        [id]: e?.message || "Failed to load image",
      }));
    } finally {
      setImgLoading((m) => ({ ...m, [id]: false }));
    }
  }

  // Fetch ALL scans for map + chart (using period filter)
  useEffect(() => {
    const ctrl = new AbortController();
    let cancelled = false;

    async function loadAll() {
      const userReady = Array.isArray(userData) && userData.length >= 6;
      if (!userReady) return;
      if (isBrandAdmin && !clientIdFromUser) return;

      try {
        setLoadingAll(true);
        setErr(null);
        setScansAll([]);

        const clientId =
          isBrandAdmin && clientIdFromUser ? clientIdFromUser : "";
        const clientParam = clientId
          ? `&client_id=${encodeURIComponent(clientId)}`
          : "";
        const res = await fetch(
          `${API_URL}?period=${encodeURIComponent(period)}${clientParam}`,
          {
            method: "GET",
            headers: {
              "x-api-key": API_KEY,
              Accept: "application/json",
            },
            signal: ctrl.signal,
          },
        );

        if (!res.ok) {
          let text = await res.text().catch(() => "");
          throw new Error(`API error ${res.status}${text ? `: ${text}` : ""}`);
        }

        const data = await res.json();
        let scansRaw = [];
        let totalAllVal = null;
        let totalFilteredVal = null;

        if (Array.isArray(data)) {
          // Backward compatibility: old endpoint returned plain array
          scansRaw = data;
        } else {
          // New shape: { period, total_all, total_filtered, scans }
          if (Array.isArray(data?.scans)) {
            scansRaw = data.scans;
          } else if (Array.isArray(data?.items)) {
            // Fallback if using some older "items" wrapper
            scansRaw = data.items;
          }
          if (typeof data.total_all === "number") {
            totalAllVal = data.total_all;
          }
          if (typeof data.total_filtered === "number") {
            totalFilteredVal = data.total_filtered;
          }
        }

        const normalized = scansRaw.map(normalizeScan);

        if (!cancelled) {
          setScansAll(normalized);
          setFitTrigger((k) => k + 1);

          setTotalAll(
            totalAllVal != null ? totalAllVal : normalized.length || 0,
          );
          setTotalFiltered(
            totalFilteredVal != null
              ? totalFilteredVal
              : normalized.length || 0,
          );
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          if (!cancelled) {
            setErr(
              e?.message ||
                "Failed to load scans. Check your API URL and CORS settings.",
            );
          }
        }
      } finally {
        if (!cancelled) setLoadingAll(false);
      }
    }

    loadAll();
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [period, isBrandAdmin, clientIdFromUser, userData]);

  // Stable page loader guarded by refs to avoid duplicate fetches
  const loadPage = useCallback(async (whichPage) => {
    if (!userReadyRef.current) return;
    if (loadingRef.current) return;
    if (loadedPagesRef.current.has(whichPage)) return;

    loadingRef.current = true;
    setLoadingPage(true);
    try {
      const clientParam = clientIdRef.current
        ? `&client_id=${encodeURIComponent(clientIdRef.current)}`
        : "";
      const res = await fetch(
        `${API_URL}/paged?page=${whichPage}${clientParam}`,
        { headers: { "x-api-key": API_KEY, Accept: "application/json" } },
      );
      if (!res.ok) {
        let text = await res.text().catch(() => "");
        throw new Error(`API error ${res.status}${text ? `: ${text}` : ""}`);
      }
      const payload = await res.json();
      const scans = Array.isArray(payload?.scans)
        ? payload.scans.map(normalizeScan) // paged includes images, normalize keeps them
        : [];
      const total_pages = Number(payload?.total_pages ?? 1);

      setScansPaged((prev) => [...prev, ...scans]);
      setTotalPages(total_pages);
      setCurrentPage((prev) => Math.max(prev, whichPage));
      loadedPagesRef.current.add(whichPage);
    } catch (e) {
      if (e.name !== "AbortError") {
        setErr(e?.message || "Failed to load paged scans.");
      }
    } finally {
      setLoadingPage(false);
      loadingRef.current = false;
    }
  }, []);

  // Initial load + identity-change reset for paged sidebar data.
  // Waits for userData to be ready before triggering any fetch.
  const prevPagedClientRef = useRef(null); // null = not yet initialized
  useEffect(() => {
    const userReady = Array.isArray(userData) && userData.length >= 6;
    if (!userReady) return;

    const activeClientId =
      isBrandAdmin && clientIdFromUser ? clientIdFromUser : "";

    if (prevPagedClientRef.current === null) {
      // First run with ready userData: store identity and trigger initial load
      prevPagedClientRef.current = activeClientId;
      loadPage(1);
      return;
    }

    if (prevPagedClientRef.current === activeClientId) return;

    // Identity changed mid-session: reset paged data and reload
    prevPagedClientRef.current = activeClientId;
    setScansPaged([]);
    setCurrentPage(0);
    setTotalPages(null);
    loadedPagesRef.current = new Set();
    loadingRef.current = false;
    loadPage(1);
  }, [isBrandAdmin, clientIdFromUser, loadPage, userData]);

  // IntersectionObserver to fetch next page when sentinel is visible
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = sidebarRef.current;
    if (!sentinel || !container) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry.isIntersecting) return;

        if (totalPages != null && currentPage >= totalPages) return;

        const next = currentPage + 1 || 1;
        loadPage(next);
      },
      {
        root: container,
        rootMargin: "0px 0px 200px 0px",
        threshold: 0.01,
      },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [currentPage, totalPages, loadPage]);

  // Keep Leaflet sized correctly after mount and when data arrives
  useEffect(() => {
    if (!mapRef.current) return;
    setTimeout(() => mapRef.current.invalidateSize(), 0);
  }, [mapRef.current, scansAll.length]);

  const openModal = (scan) => {
    const full = scansAll.find((s) => s._id === scan._id) || scan;
    setModalScan(full);
    fetchImageForId(full._id);
  };

  const handleFitAll = () => {
    setFitTrigger((k) => k + 1);
  };

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setScansPaged([]);
    setCurrentPage(0);
    setTotalPages(null);
    loadedPagesRef.current = new Set();
    loadingRef.current = false;
    await loadPage(1);
    setRefreshing(false);
  }, [loadPage]);

  // Points for bounds fit (from ALL scans for better coverage)
  const points = useMemo(
    () =>
      scansAll
        .map((s) => [s.coordinates?.lat, s.coordinates?.lng])
        .filter((p) => Number.isFinite(p[0]) && Number.isFinite(p[1])),
    [scansAll],
  );

  // Build chart data from ALL scans (filtered by current period)
  const series = useMemo(() => makeDailySeries(scansAll), [scansAll]);
  const lineData = useMemo(
    () => ({
      labels: series.labels,
      datasets: [
        {
          label: "Genuine",
          data: series.genuine,
          borderColor: "#22c55e",
          backgroundColor: "rgba(34,197,94,0.15)",
          tension: 0.3,
          fill: true,
        },
        {
          label: "Copy",
          data: series.copy,
          borderColor: "#ef4444",
          backgroundColor: "rgba(239,68,68,0.15)",
          tension: 0.3,
          fill: true,
        },
        {
          label: "Invalid",
          data: series.invalid,
          borderColor: "#9ca3af",
          backgroundColor: "rgba(156,163,175,0.20)",
          tension: 0.3,
          fill: true,
        },
      ],
    }),
    [series],
  );

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: true, position: "top" },
        tooltip: { mode: "index", intersect: false },
      },
      interaction: { mode: "nearest", axis: "x", intersect: false },
      scales: {
        x: {
          ticks: {
            callback: (v, i) => {
              const label = series.labels[i] || "";
              const d = new Date(label + "T00:00:00Z");
              return d.toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              });
            },
          },
          grid: { display: false },
        },
        y: {
          beginAtZero: true,
          grid: { color: "rgba(0,0,0,0.05)" },
          ticks: { precision: 0 },
        },
      },
    }),
    [series.labels],
  );

  return (
    <>
      <Helmet>
        <title>Tag Analytics | BLEEP</title>
        <meta
          name="description"
          content="BLEEP Tag Analytics. View scan locations and details."
        />
      </Helmet>

      <div
        className="d-flex"
        style={{ height: `calc(100vh - ${NAVBAR_H}px)` }}
      >
        <Sidenav domain={domain} />

        <div
          style={{
            flex: 1,
            height: `calc(100vh - ${NAVBAR_H}px)`,
            display: "flex",
            overflow: "hidden",
            position: "relative",
            backgroundColor: "#f5f5f5",
          }}
        >
          {/* Map column */}
          <div
            style={{
              position: "relative",
              flex: 1,
              height: `calc(100vh - ${NAVBAR_H}px)`,
            }}
          >
            <MapContainer
              whenCreated={(m) => (mapRef.current = m)}
              center={[0.3476, 32.5825]}
              zoom={7}
              scrollWheelZoom
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
              }}
            >
              {/* Hybrid: Satellite imagery base + reference labels/roads overlay */}
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                attribution="Tiles &copy; Esri &mdash; Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community"
              />
              {/* Put labels above imagery and below markers. Prevent the overlay from intercepting clicks. */}
              <Pane
                name="labels"
                style={{ zIndex: 650, pointerEvents: "none" }}
              >
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  attribution="Labels &copy; Esri"
                />
              </Pane>

              {/* Fit to data when triggered, fallback to Uganda */}
              <FitToData
                trigger={fitTrigger}
                points={points}
              />

              {loadingAll && (
                <div
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "40%",
                    zIndex: 1200,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    background: "rgba(255,255,255,0.9)",
                    padding: "6px 10px",
                    borderRadius: 8,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
                    fontSize: 13,
                  }}
                >
                  <i className="fas fa-spinner fa-spin" />
                  Loading map
                </div>
              )}

              {scansAll.map((scan) => {
                const { lat, lng } = scan.coordinates || {};
                if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
                  return null;
                }
                const pos = [lat, lng];
                const status = getStatus(scan);
                const color = markerColor(status);

                return (
                  <CircleMarker
                    key={scan._id}
                    center={pos}
                    pathOptions={{ color, fillColor: color, fillOpacity: 0.85 }}
                    radius={9}
                    eventHandlers={{
                      click: () => openModal(scan),
                    }}
                  />
                );
              })}
            </MapContainer>

            {/* Floating fit button */}
            <button
              type="button"
              onClick={handleFitAll}
              title="Zoom to show all markers"
              aria-label="Zoom to show all markers"
              style={{
                position: "absolute",
                right: 20,
                top: 40,
                width: 56,
                height: 56,
                borderRadius: "50%",
                border: "none",
                outline: "none",
                background: "#ffffff",
                boxShadow: "0 6px 18px rgba(0,0,0,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                zIndex: 1000,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.boxShadow =
                  "0 8px 24px rgba(0,0,0,0.25)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.2)")
              }
            >
              <i
                className="fas fa-expand-arrows-alt"
                style={{ fontSize: 24, color: "#000000" }}
              />
            </button>
          </div>

          {/* Right sidebar */}
          <aside
            ref={sidebarRef}
            style={{
              width: SIDEBAR_W,
              minWidth: SIDEBAR_W,
              maxWidth: SIDEBAR_W,
              height: `calc(100vh - ${NAVBAR_H}px)`,
              overflowY: "auto",
              background: "#ffffff",
              borderLeft: "1px solid #e6e6e6",
              boxShadow: "0 0 24px rgba(0,0,0,0.05)",
              position: "relative",
            }}
          >
            {/* List header */}
            <div
              style={{
                padding: "16px 16px 8px 16px",
                borderBottom: "1px solid #f3f4f6",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 8,
                }}
              >
                <div>
                  <h5
                    style={{
                      marginBottom: 4,
                      display: "flex",
                      gap: 8,
                      alignItems: "center", // NEW: vertically center text + spinner
                    }}
                  >
                    <b>
                      {loadingAll
                        ? "Loading scans..."
                        : `${totalFiltered ?? scansAll.length} Scan${
                            (totalFiltered ?? scansAll.length) !== 1 ? "s" : ""
                          }`}
                    </b>
                    {(loadingAll || loadingPage) && (
                      <i
                        className="fas fa-spinner fa-spin"
                        aria-label="Loading"
                        style={{
                          color: "#64748b",
                          display: "inline-block",
                          lineHeight: 1,
                          fontSize: 14,
                          transformOrigin: "50% 50%", // NEW: spin around center
                        }}
                      />
                    )}
                  </h5>
                  <div
                    style={{
                      fontSize: 12,
                      color: "#6b7280",
                    }}
                  >
                    All time:{" "}
                    <b>{totalAll != null ? totalAll : scansAll.length}</b>
                    <br />
                    Filtered:{" "}
                    <b>
                      {totalFiltered != null ? totalFiltered : scansAll.length}
                    </b>
                  </div>
                  {err && (
                    <div style={{ color: "#b91c1c", fontSize: 12 }}>{err}</div>
                  )}
                </div>

                {/* Period dropdown */}
                <div>
                  <label
                    htmlFor="period-select"
                    style={{
                      fontSize: 11,
                      textTransform: "uppercase",
                      letterSpacing: 0.04,
                      color: "#000000",
                      display: "block",
                      marginBottom: 4,
                    }}
                  >
                    Period
                  </label>
                  <select
                    id="period-select"
                    value={period}
                    onChange={(e) => setPeriod(e.target.value)}
                    style={{
                      fontSize: 12,
                      padding: "4px 8px",
                      borderRadius: 999,
                      border: "1px solid #d1d5db",
                      backgroundColor: "#f9fafb",
                      color: "#000000",
                      cursor: "pointer",
                      outline: "none",
                    }}
                  >
                    <option value="today">Today</option>
                    <option value="week">Last 7 days</option>
                    <option value="month">Last 30 days</option>
                    <option value="year">Last 12 months</option>
                    <option value="all">All time</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Analytics card */}
            <div style={{ padding: "12px 16px 12px 16px" }}>
              <div style={{ height: 220 }}>
                {loadingAll ? (
                  <div
                    className="text-muted"
                    style={{
                      fontSize: 13,
                      textAlign: "center",
                      paddingTop: 80,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 8,
                    }}
                  >
                    <i className="fas fa-spinner fa-spin" />
                    Preparing chart...
                  </div>
                ) : series.labels.length ? (
                  <Line
                    data={lineData}
                    options={lineOptions}
                  />
                ) : (
                  <div
                    className="text-muted"
                    style={{
                      fontSize: 13,
                      textAlign: "center",
                      paddingTop: 80,
                    }}
                  >
                    No data to show.
                  </div>
                )}
              </div>
            </div>

            {/* Recent scans heading + refresh */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "4px 16px 10px",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 13, color: "#0f172a" }}>
                Recent scans
              </span>
              <button
                type="button"
                onClick={handleRefresh}
                disabled={refreshing || loadingPage}
                title="Refresh scans"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid #d1d5db",
                  backgroundColor: "#f9fafb",
                  color: "#374151",
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: refreshing || loadingPage ? "not-allowed" : "pointer",
                  opacity: refreshing || loadingPage ? 0.6 : 1,
                  outline: "none",
                }}
              >
                <i
                  className={`fas fa-rotate-right${refreshing ? " fa-spin" : ""}`}
                  style={{ fontSize: 11 }}
                />
                Refresh
              </button>
            </div>

            {/* Cards */}
            <div style={{ paddingInline: 16, paddingBottom: 16 }}>
              {!loadingPage && scansPaged.length === 0 && !err && (
                <div
                  className="text-muted"
                  style={{
                    padding: 16,
                    fontSize: 14,
                    display: "flex",
                    gap: 8,
                    alignItems: "center",
                  }}
                >
                  <i className="fas fa-info-circle" />
                  No scans yet, scanned security tags will appear here.
                </div>
              )}

              {scansPaged.map((scan) => {
                const status = getStatus(scan);
                const chip = chipColors(status);
                const { lat, lng } = scan.coordinates || {};

                const id = scan._id;
                const isLoading = !!imgLoading[id];
                const loadErr = imgError[id];
                const thumbUrl = scan.images?.raw?.url || null; // only show if provided by /paged

                const count = Number.isFinite(Number(scan.serial_count))
                  ? Number(scan.serial_count)
                  : 1;

                return (
                  <div
                    key={scan._id}
                    className="card mb-3"
                    style={{
                      borderRadius: 18,
                      overflow: "hidden",
                      cursor: "pointer",
                      background: "#fff",
                      border: "1px solid #eef2f7",
                      boxShadow: "0 10px 28px rgba(2, 6, 23, 0.08)",
                      transition: "transform 140ms ease, box-shadow 140ms ease",
                    }}
                    onClick={() => openModal(scan)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-3px)";
                      e.currentTarget.style.boxShadow =
                        "0 14px 40px rgba(2, 6, 23, 0.12)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 10px 28px rgba(2, 6, 23, 0.08)";
                    }}
                  >
                    {/* Media */}
                    <div style={{ position: "relative" }}>
                      {thumbUrl ? (
                        <img
                          src={thumbUrl}
                          alt={`${scan.serial} image`}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: 168,
                            objectFit: "cover",
                            display: "block",
                            cursor: "pointer",
                            filter: isLoading
                              ? "blur(1px) saturate(0.9)"
                              : "none",
                            transform: "translateZ(0)",
                          }}
                        />
                      ) : (
                        <div
                          style={{
                            height: 168,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background:
                              "linear-gradient(180deg, rgba(241,245,249,1) 0%, rgba(248,250,252,1) 100%)",
                            color: "#64748b",
                            fontSize: 13,
                          }}
                        >
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 8,
                            }}
                          >
                            <i className="far fa-image" />
                            No image
                          </span>
                        </div>
                      )}

                      {/* Subtle top gradient */}
                      <div
                        style={{
                          position: "absolute",
                          inset: 0,
                          background:
                            "linear-gradient(180deg, rgba(2,6,23,0.25) 0%, rgba(2,6,23,0) 55%)",
                          pointerEvents: "none",
                          opacity: thumbUrl ? 1 : 0,
                        }}
                      />

                      {/* Date + time pinned over image (no truncation) */}
                      <div
                        style={{
                          position: "absolute",
                          top: 10,
                          left: 10,
                          display: "flex",
                          gap: 8,
                          alignItems: "center",
                          flexWrap: "wrap",
                          maxWidth: "calc(100% - 20px)",
                        }}
                        title="Scan time"
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "6px 10px",
                            borderRadius: 999,
                            background: "rgba(255,255,255,0.88)",
                            color: "#0f172a",
                            fontWeight: 800,
                            fontSize: 12,
                            lineHeight: 1,
                            boxShadow: "0 6px 16px rgba(2,6,23,0.14)",
                            border: "1px solid rgba(2,6,23,0.06)",
                            backdropFilter: "blur(6px)",
                            whiteSpace: "normal",
                            overflow: "visible",
                            textOverflow: "clip",
                            maxWidth: "100%",
                          }}
                        >
                          <i
                            className="far fa-clock"
                            style={{ opacity: 0.85 }}
                          />
                          {isNaN(new Date(scan.timestamp))
                            ? "No timestamp"
                            : new Date(scan.timestamp).toLocaleString()}
                        </span>
                      </div>

                      {/* Loading overlay */}
                      {isLoading && (
                        <div
                          style={{
                            position: "absolute",
                            inset: 0,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(255,255,255,0.65)",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "10px 14px",
                              borderRadius: 999,
                              background: "#fff",
                              border: "1px solid #e5e7eb",
                              boxShadow: "0 10px 24px rgba(2, 6, 23, 0.10)",
                              color: "#0f172a",
                              fontWeight: 700,
                              fontSize: 13,
                            }}
                          >
                            <i className="fas fa-spinner fa-spin" />
                            Loading…
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    <div
                      className="card-body"
                      style={{
                        padding: 14,
                        backgroundColor: "#fff",
                        color: "#0f172a",
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <div
                          style={{
                            fontWeight: 900,
                            fontSize: 14,
                            lineHeight: 1.15,
                          }}
                        >
                          {scan.serial !== "" && scan.serial.length < 10
                            ? `Serial ${scan.serial}`
                            : "Invalid scan"}
                        </div>

                        {/* Genuine/Copy + Scans under serial */}
                        <div
                          style={{
                            marginTop: 8,
                            display: "flex",
                            gap: 8,
                            alignItems: "center",
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              padding: "6px 10px",
                              borderRadius: 999,
                              background: chip.bg,
                              color: chip.text,
                              fontWeight: 850,
                              fontSize: 12,
                              lineHeight: 1,
                              border: "1px solid rgba(2,6,23,0.06)",
                            }}
                          >
                            {chip.label}
                          </span>

                          {scan.serial !== "" && scan.serial.length < 10 ? (
                            <span
                              title="Total times this serial was scanned"
                              style={{
                                padding: "6px 10px",
                                borderRadius: 999,
                                background: "#f1f5f9",
                                color: "#0f172a",
                                fontWeight: 850,
                                fontSize: 12,
                                lineHeight: 1,
                                border: "1px solid #e6e9ee",
                              }}
                            >
                              Scans: {count}
                            </span>
                          ) : null}
                        </div>
                      </div>

                      {/* Client/Product block */}
                      <div
                        style={{
                          marginTop: 12,
                          background: "#f8fafc",
                          border: "1px solid #e6e9ee",
                          borderRadius: 14,
                          padding: 12,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div style={{ display: "grid", gap: 6 }}>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "baseline",
                              justifyContent: "space-between",
                              gap: 10,
                              flexWrap: "wrap",
                            }}
                          >
                            <div
                              style={{
                                color: "#64748b",
                                fontSize: 11,
                                fontWeight: 900,
                              }}
                            >
                              CLIENT
                            </div>
                            {/* <button
                              type="button"
                              onClick={() =>
                                alert(
                                  `Coming Soon`,
                                )
                              }
                              style={{
                                appearance: "none",
                                border: "1px solid #e5e7eb",
                                background: "#fff",
                                color: "#0f172a",
                                borderRadius: 999,
                                padding: "4px 10px",
                                fontSize: 11,
                                fontWeight: 800,
                                cursor: "pointer",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                              }}
                            >
                              More <i className="fas fa-external-link-alt" />
                            </button> */}
                          </div>

                          <div
                            style={{
                              color: "#0f172a",
                              fontSize: 13,
                              fontWeight: 800,
                              lineHeight: 1.25,
                              wordBreak: "break-word",
                            }}
                          >
                            {scan.client_name || "Unknown"}
                          </div>
                        </div>

                        <div style={{ height: 1, background: "#e6e9ee" }} />

                        <div style={{ display: "grid", gap: 6 }}>
                          <div
                            style={{
                              color: "#64748b",
                              fontSize: 11,
                              fontWeight: 900,
                            }}
                          >
                            PRODUCT
                          </div>

                          <div
                            style={{
                              color: "#0f172a",
                              fontSize: 13,
                              fontWeight: 800,
                              lineHeight: 1.25,
                              wordBreak: "break-word",
                            }}
                          >
                            {scan.product_name || "Unknown"}
                          </div>
                        </div>
                      </div>

                      {loadErr && (
                        <div
                          style={{
                            marginTop: 10,
                            padding: "8px 10px",
                            borderRadius: 12,
                            background: "rgba(185, 28, 28, 0.08)",
                            border: "1px solid rgba(185, 28, 28, 0.18)",
                            color: "#b91c1c",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {loadErr}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Loader / End-of-list indicator */}
              <div
                ref={sentinelRef}
                style={{
                  padding: 16,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#64748b",
                  gap: 8,
                }}
              >
                {loadingPage ? (
                  <>
                    <i className="fas fa-spinner fa-spin" />
                    <span style={{ fontSize: 13 }}>Loading scans…</span>
                  </>
                ) : totalPages != null && currentPage >= totalPages ? (
                  <span style={{ fontSize: 12 }}>You are all caught up.</span>
                ) : (
                  <span style={{ fontSize: 12, opacity: 0.6 }}>
                    Scroll to load more…
                  </span>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      {/* Scan info modal */}
      <Modal
        show={!!modalScan}
        onHide={closeModal}
        centered
        scrollable
        size="lg"
        animation={false}
      >
        {modalScan &&
          (() => {
            const mStatus = getStatus(modalScan);
            const mChip = chipColors(mStatus);
            const mColor = markerColor(mStatus);
            const mLat = modalScan.coordinates?.lat;
            const mLng = modalScan.coordinates?.lng;
            const hasCoords = Number.isFinite(mLat) && Number.isFinite(mLng);
            const mCount = Number.isFinite(Number(modalScan.serial_count))
              ? Number(modalScan.serial_count)
              : 1;
            const mImgUrl =
              imgById[modalScan._id] || modalScan.images?.raw?.url || null;
            const mImgLoading = !!imgLoading[modalScan._id];
            const mImgErr = imgError[modalScan._id];
            const isValidSerial =
              modalScan.serial !== "" && modalScan.serial.length < 10;

            return (
              <>
                {/* Header: scan ID is the title, click to copy */}
                <Modal.Header
                  closeButton
                  style={{
                    borderBottom: "1px solid #f3f4f6",
                    padding: "12px 20px",
                  }}
                >
                  <button
                    onClick={copyId}
                    title="Click to copy scan ID"
                    style={{
                      background: "none",
                      border: "none",
                      padding: 0,
                      cursor: "pointer",
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <code
                      style={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: "#0f172a",
                        letterSpacing: 0.2,
                      }}
                    >
                      {modalScan._id}
                    </code>
                    <i
                      className={idCopied ? "fas fa-check" : "fas fa-copy"}
                      style={{
                        fontSize: 12,
                        color: idCopied ? "#22c55e" : "#94a3b8",
                      }}
                    />
                  </button>
                </Modal.Header>

                <Modal.Body style={{ padding: 20 }}>
                  {/* Top: image card (left) + details (right) */}
                  <div
                    style={{
                      display: "flex",
                      gap: 16,
                      alignItems: "stretch",
                      marginBottom: 16,
                    }}
                  >
                    {/* Image card */}
                    <div
                      style={{
                        width: 210,
                        minWidth: 210,
                        flexShrink: 0,
                        borderRadius: 12,
                        overflow: "hidden",
                        border: "1px solid #e6e9ee",
                        background: "#111827",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 160,
                      }}
                    >
                      {mImgUrl ? (
                        <img
                          src={mImgUrl}
                          alt={`Serial ${modalScan.serial}`}
                          onClick={() =>
                            openLightbox(mImgUrl, `Serial ${modalScan.serial}`)
                          }
                          style={{
                            display: "block",
                            maxWidth: "100%",
                            maxHeight: 240,
                            width: "auto",
                            height: "auto",
                            cursor: "zoom-in",
                          }}
                        />
                      ) : mImgLoading ? (
                        <div
                          style={{
                            color: "#64748b",
                            fontSize: 13,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <i
                            className="fas fa-spinner fa-spin"
                            style={{ fontSize: 22 }}
                          />
                          <span>Loading…</span>
                        </div>
                      ) : (
                        <div
                          style={{
                            color: "#ffffff",
                            fontSize: 13,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 8,
                            opacity: 0.3,
                          }}
                        >
                          <i
                            className="far fa-image"
                            style={{ fontSize: 30 }}
                          />
                          <span>No image</span>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div
                      style={{
                        flex: 1,
                        display: "flex",
                        flexDirection: "column",
                        gap: 10,
                        minWidth: 0,
                      }}
                    >
                      {/* Serial + status */}
                      <div>
                        <div
                          style={{
                            fontWeight: 900,
                            fontSize: 15,
                            color: "#0f172a",
                            marginBottom: 8,
                          }}
                        >
                          {isValidSerial
                            ? `Serial ${modalScan.serial}`
                            : "Invalid scan"}
                        </div>
                        <div
                          style={{ display: "flex", gap: 7, flexWrap: "wrap" }}
                        >
                          <span
                            style={{
                              padding: "5px 10px",
                              borderRadius: 999,
                              background: mChip.bg,
                              color: mChip.text,
                              fontWeight: 850,
                              fontSize: 12,
                              border: "1px solid rgba(2,6,23,0.06)",
                            }}
                          >
                            {mChip.label}
                          </span>
                          {isValidSerial && (
                            <span
                              title="Total times this serial was scanned"
                              style={{
                                padding: "5px 10px",
                                borderRadius: 999,
                                background: "#f1f5f9",
                                color: "#0f172a",
                                fontWeight: 850,
                                fontSize: 12,
                                border: "1px solid #e6e9ee",
                              }}
                            >
                              Scans: {mCount}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Timestamp */}
                      {!isNaN(new Date(modalScan.timestamp)) && (
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            color: "#64748b",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          <i className="far fa-clock" />
                          {new Date(modalScan.timestamp).toLocaleString()}
                        </div>
                      )}

                      {/* Client / Product */}
                      <div
                        style={{
                          background: "#f8fafc",
                          border: "1px solid #e6e9ee",
                          borderRadius: 12,
                          padding: "10px 12px",
                          display: "grid",
                          gap: 8,
                          flex: 1,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              color: "#64748b",
                              fontSize: 10,
                              fontWeight: 900,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              marginBottom: 2,
                            }}
                          >
                            Client
                          </div>
                          <div
                            style={{
                              color: "#0f172a",
                              fontSize: 13,
                              fontWeight: 800,
                            }}
                          >
                            {modalScan.client_name || "Unknown"}
                          </div>
                        </div>
                        <div style={{ height: 1, background: "#e6e9ee" }} />
                        <div>
                          <div
                            style={{
                              color: "#64748b",
                              fontSize: 10,
                              fontWeight: 900,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                              marginBottom: 2,
                            }}
                          >
                            Product
                          </div>
                          <div
                            style={{
                              color: "#0f172a",
                              fontSize: 13,
                              fontWeight: 800,
                            }}
                          >
                            {modalScan.product_name || "Unknown"}
                          </div>
                        </div>
                      </div>

                      {mImgErr && (
                        <div
                          style={{
                            padding: "7px 10px",
                            borderRadius: 8,
                            background: "rgba(185,28,28,0.08)",
                            border: "1px solid rgba(185,28,28,0.18)",
                            color: "#b91c1c",
                            fontSize: 12,
                            fontWeight: 700,
                          }}
                        >
                          {mImgErr}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location map */}
                  {hasCoords && (
                    <div style={{ position: "relative" }}>
                      <div
                        style={{
                          borderRadius: 12,
                          overflow: "hidden",
                          height: 240,
                          border: "1px solid #e6e9ee",
                        }}
                      >
                        <MapContainer
                          key={modalScan._id}
                          center={[mLat, mLng]}
                          zoom={14}
                          scrollWheelZoom
                          attributionControl={false}
                          style={{ width: "100%", height: "100%" }}
                        >
                          <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
                          <Pane
                            name="modal-labels"
                            style={{ zIndex: 650, pointerEvents: "none" }}
                          >
                            <TileLayer url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}" />
                          </Pane>
                          <CircleMarker
                            center={[mLat, mLng]}
                            pathOptions={{
                              color: mColor,
                              fillColor: mColor,
                              fillOpacity: 0.9,
                            }}
                            radius={10}
                          />
                        </MapContainer>
                      </div>

                      {/* Map overlay actions */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: 10,
                          left: 10,
                          zIndex: 1100,
                          display: "flex",
                          gap: 6,
                        }}
                      >
                        <button
                          onClick={() => copyCoords(mLat, mLng)}
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 11px",
                            borderRadius: 999,
                            background: "rgba(255,255,255,0.93)",
                            border: "1px solid rgba(0,0,0,0.09)",
                            color: "#0f172a",
                            fontWeight: 700,
                            fontSize: 12,
                            cursor: "pointer",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <i
                            className={
                              coordsCopied ? "fas fa-check" : "fas fa-copy"
                            }
                            style={{
                              color: coordsCopied ? "#22c55e" : "#64748b",
                              fontSize: 11,
                            }}
                          />
                          {coordsCopied
                            ? "Copied!"
                            : `${mLat.toFixed(5)}, ${mLng.toFixed(5)}`}
                        </button>
                        <a
                          href={`https://maps.google.com/?q=${mLat},${mLng}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "6px 11px",
                            borderRadius: 999,
                            background: "rgba(255,255,255,0.93)",
                            border: "1px solid rgba(0,0,0,0.09)",
                            color: "#0f172a",
                            fontWeight: 700,
                            fontSize: 12,
                            textDecoration: "none",
                            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
                            backdropFilter: "blur(4px)",
                          }}
                        >
                          <i
                            className="fas fa-external-link-alt"
                            style={{ color: "#64748b", fontSize: 11 }}
                          />
                          Google Maps
                        </a>
                      </div>
                    </div>
                  )}
                </Modal.Body>
              </>
            );
          })()}
      </Modal>

      {/* Fullscreen lightbox — covers full viewport including navbar */}
      {lightbox.open && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={closeLightbox}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.92)",
            backdropFilter: "blur(2px)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 24,
          }}
        >
          <img
            src={lightbox.src}
            alt={lightbox.alt}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: "92vw",
              maxHeight: "92vh",
              objectFit: "contain",
              borderRadius: 12,
              boxShadow: "0 20px 60px rgba(0,0,0,0.5)",
            }}
          />
          {/* Close button */}
          <button
            onClick={closeLightbox}
            aria-label="Close"
            style={{
              position: "absolute",
              top: 16,
              right: 16,
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "none",
              background: "rgba(255,255,255,0.9)",
              boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
              color: "#000",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ fontSize: 18, lineHeight: 1 }}>✕</span>
          </button>
        </div>
      )}
    </>
  );
};

export default BleSTMap;
